// @flow

import React, { useState, useMemo } from "react";

import type { FormBuilderField } from "src/types";

import { Table, Tbody } from "@chakra-ui/react";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Form } from "src/styles/form.old";
import { Builder as StyledBuilder } from "src/styles/popover.old";
import { Fields as StyledFields } from "src/styles/checklist";
import { TextButtonWithImageAndBorder } from "src/styles/buttons.old";
import Icon from "src/components/InlineSVG";
import { iconSize } from "src/styles/constants";
import * as colors from "src/styles/constants/colors";

import Field from "./Field";
import NewFieldModal from "./NewFieldModal";

const FieldItem = ({
  id,
  value,
  index,
  addFieldBelow,
  removeField,
  updateField,
}) => {
  const { listeners, setNodeRef, setActivatorNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Field
      value={value}
      position={index}
      addFieldBelow={addFieldBelow}
      removeField={removeField}
      updateField={updateField}
      dragHandleRef={setActivatorNodeRef}
      dragHandleListeners={listeners}
      rowRef={setNodeRef}
      style={style}
    />
  );
};

const FieldList = ({
  items,
  itemIds,
  addFieldBelow,
  removeField,
  updateField,
}) => {
  return (
    <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
      <Tbody>
        {items.map((value, index) => (
          <FieldItem
            key={`item-${index}`}
            id={itemIds[index]}
            value={value}
            index={index}
            addFieldBelow={addFieldBelow}
            removeField={removeField}
            updateField={updateField}
          />
        ))}
      </Tbody>
    </SortableContext>
  );
};

type Props = {
  items: FormBuilderField[],
  addField: Function,
  removeField: Function,
  updateField: Function,
};

export default function Fields({
  items,
  addField,
  removeField,
  updateField,
}: Props) {
  const [addFieldModalData, setAddFieldModalData] = useState({
    isOpen: false,
    index: null,
  });

  const closeAddFieldModal = () => {
    setAddFieldModalData({
      isOpen: false,
      index: null,
    });
  };

  const selectFieldType = (fieldType) => {
    const { index } = addFieldModalData;
    addField({
      fieldType,
      index,
    });

    closeAddFieldModal();
  };

  const addFieldBelowField = (index) => {
    setAddFieldModalData({
      isOpen: true,
      index,
    });
  };

  const itemIds = useMemo(
    () => items.map((v, i) => (v.fieldId ? String(v.fieldId) : `idx-${i}`)),
    [items],
  );

  return (
    <StyledBuilder>
      <Form>
        <StyledFields>
          <Table variant="unstyled" size="sm">
            <FieldList
              items={items}
              itemIds={itemIds}
              addFieldBelow={addFieldBelowField}
              removeField={removeField}
              updateField={updateField}
            />
          </Table>
        </StyledFields>
      </Form>
      <TextButtonWithImageAndBorder
        type="button"
        onClick={() =>
          setAddFieldModalData({
            isOpen: true,
            index: null,
          })
        }
      >
        <Icon
          icon="hollowPlusCircle"
          color={colors.blue500}
          size={iconSize.big}
        />

        <p>Add Fields</p>
      </TextButtonWithImageAndBorder>

      <NewFieldModal
        isOpen={addFieldModalData.isOpen}
        onClose={closeAddFieldModal}
        onSelect={selectFieldType}
      />
    </StyledBuilder>
  );
}
